import { ArrowRightOutlined, CheckCircleOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { Modal, Form, Input, Switch, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_AI_INFERENCE_POLICY = "STANDARD_BEDROCK_CONTROLLED";

const COMMERCIAL_PLAN_OPTIONS = ["trial", "entrepreneur", "solo", "pro", "enterprise"];

function buildCommercialSubscriptionPath(clientId) {
    return `/admin/commercial?tab=subscriptions&client_id=${encodeURIComponent(clientId)}`;
}

const ClientFormModal = ({ open, onClose, onSubmit, initialValues }) => {
    const { t } = useTranslation("clients");
    const [form] = Form.useForm();
    const activeValue = Form.useWatch("is_active", form);
    const aiInferencePolicyValue = Form.useWatch("ai_inference_policy", form);

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                ...initialValues,
                is_active: initialValues.is_active ?? true,
                ai_inference_policy: initialValues.ai_inference_policy ?? DEFAULT_AI_INFERENCE_POLICY,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                is_active: true,
                ai_inference_policy: DEFAULT_AI_INFERENCE_POLICY,
            });
        }
    }, [initialValues, form]);

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            const { subscription_plan, commercial_plan_code, ...restValues } = values;
            const safeValues = initialValues
                ? restValues
                : { ...restValues, commercial_plan_code };
            const success = await onSubmit(safeValues);
            if (success) {
                form.resetFields();
            }
        } catch (e) {
        }
    };

    const toggleActive = () => {
        form.setFieldsValue({ is_active: !(activeValue ?? true) });
    };

    return (
        <Modal
            title={initialValues ? t("form.editTitle") : t("form.addTitle")}
            open={open}
            onCancel={onClose}
            onOk={handleOk}
            centered
            destroyOnHidden
            okText={initialValues ? t("form.update") : t("form.create")}
            okButtonProps={{ className: "verdictia-primary-btn" }}
            className="client-form-modal"
            width="min(820px, calc(100vw - 32px))"
        >
            <Form
                layout="vertical"
                form={form}
                initialValues={{
                    is_active: true,
                    ai_inference_policy: DEFAULT_AI_INFERENCE_POLICY,
                }}
            >
                <div className="client-form-grid">
                    <div className="client-form-column">
                        <Form.Item
                            name="name"
                            label={t("form.name")}
                            rules={[{ required: true, message: t("form.required") }]}
                        >
                            <Input placeholder={t("form.namePlaceholder")} />
                        </Form.Item>

                        <Form.Item
                            name="contact_email"
                            label={t("form.email")}
                            rules={[{ type: "email", message: t("form.invalidEmail") }]}
                        >
                            <Input placeholder={t("form.emailPlaceholder")} />
                        </Form.Item>

                        <Form.Item name="industry" label={t("form.industry")}>
                            <Input placeholder={t("form.industryPlaceholder")} />
                        </Form.Item>
                    </div>

                    <div className="client-form-column client-form-column--settings">
                        <div className="client-form-workspace-plan-note">
                            <span className="client-form-workspace-plan-note__icon" aria-hidden="true">
                                <SafetyCertificateOutlined />
                            </span>
                            <span className="client-form-workspace-plan-note__content">
                                <strong>{t("form.workspacePlanTitle")}</strong>
                                <small>{t("form.workspacePlanHelp")}</small>
                                {initialValues?.subscription_plan && (
                                    <em>{t("form.legacyPlanFallback")}</em>
                                )}
                                {initialValues?.id ? (
                                    <a
                                        className="client-form-workspace-plan-note__action"
                                        href={buildCommercialSubscriptionPath(initialValues.id)}
                                    >
                                        <span>{t("form.workspacePlanEditCta")}</span>
                                        <ArrowRightOutlined aria-hidden="true" />
                                    </a>
                                ) : (
                                    <em>{t("form.workspacePlanCreateHint")}</em>
                                )}
                            </span>
                        </div>

                        {!initialValues && (
                            <Form.Item
                                name="commercial_plan_code"
                                label={t("form.commercialPlan")}
                                rules={[{ required: true, message: t("form.commercialPlanRequired") }]}
                            >
                                <Select
                                    showSearch
                                    placeholder={t("form.commercialPlanPlaceholder")}
                                    optionFilterProp="label"
                                    options={COMMERCIAL_PLAN_OPTIONS.map((planCode) => ({
                                        value: planCode,
                                        label: t(`form.commercialPlanOptions.${planCode}`),
                                    }))}
                                />
                            </Form.Item>
                        )}

                        <Form.Item
                            name="ai_inference_policy"
                            label={t("form.aiPolicy")}
                            extra={t("form.aiPolicyHelp")}
                        >
                            <Select
                                placeholder={t("form.aiPolicyPlaceholder")}
                                options={[
                                    {
                                        value: "STANDARD_BEDROCK_CONTROLLED",
                                        label: t("form.aiPolicyStandard"),
                                    },
                                    {
                                        value: "CANADA_ONLY",
                                        label: t("form.aiPolicyCanadaOnly"),
                                        disabled: true,
                                    },
                                ]}
                            />
                        </Form.Item>

                        {aiInferencePolicyValue === "CANADA_ONLY" && (
                            <div className="client-form-ai-policy-warning">
                                {t("form.aiPolicyCanadaWarning")}
                            </div>
                        )}

                        <Form.Item className="client-form-field--state">
                            <div
                                role="button"
                                tabIndex={0}
                                className={`client-form-state-card ${(activeValue ?? true) ? "client-form-state-card--active" : ""}`}
                                onClick={toggleActive}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        toggleActive();
                                    }
                                }}
                            >
                                <span className="client-form-state-card__icon" aria-hidden="true">
                                    <CheckCircleOutlined />
                                </span>
                                <span className="client-form-state-card__content">
                                    <span>{t("form.active")}</span>
                                    <small>
                                        {(activeValue ?? true) ? t("status.active") : t("status.inactive")}
                                    </small>
                                </span>
                                <span onClick={(event) => event.stopPropagation()}>
                                    <Form.Item name="is_active" valuePropName="checked" noStyle>
                                        <Switch
                                            checked={activeValue ?? true}
                                            onChange={(checked) => form.setFieldsValue({ is_active: checked })}
                                            size="small"
                                        />
                                    </Form.Item>
                                </span>
                            </div>
                        </Form.Item>

                        <Form.Item name="custom_settings" hidden>
                            <Input />
                        </Form.Item>
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default ClientFormModal;
